import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { EndPoints } from '../../axios/endPoints';
import { axiosInstance } from '../../axios/axiosHandler';
import { authHeader } from '../auth-slice/auth-slice';

// Async thunk to fetch optimization columns for a specific training dataset
export const GetOptimizationColumns = createAsyncThunk(
    'GetOptimizationColumns',
    async (id: number, thunkAPI) => {
        try {
            // Send a GET request to fetch optimization columns
            const response = await axiosInstance.get(
                EndPoints.ML_CREATOR_GET_TRAINING_MODEL_DETAILS + `?id=${id}`,
                authHeader()
            );
            return await response.data;
        } catch (error) {
            // Return a rejected value if the request fails
            return thunkAPI.rejectWithValue(null);
        }
    }
);

// Async thunk to optimize a single input
export const OptimizeSingle = createAsyncThunk(
    'OptimizeSingle',
    async (request: any, thunkAPI) => {
        try {
            // Send a POST request to optimize a single input
            const response = await axiosInstance.post(
                EndPoints.ML_TESTER_OPTIMIZE_SINGLE,
                request,
                authHeader()
            );
            return await response.data;
        } catch (error) {
            // Return a rejected value if the request fails
            return thunkAPI.rejectWithValue(null);
        }
    }
);

// Async thunk to fetch all optimization models
export const GetOptimizationModels = createAsyncThunk(
    'GetOptimizationModels',
    async (_, thunkAPI) => {
        try {
            // Send a GET request to fetch optimization models
            const response = await axiosInstance.get(
                EndPoints.OPTIMIZATION_GET_MODELS,
                authHeader()
            );
            return await response.data;
        } catch (error) {
            // Return a rejected value if the request fails
            return thunkAPI.rejectWithValue(null);
        }
    }
);

// Async thunk to delete an optimization by ID
export const DeleteOptimization = createAsyncThunk(
    'DeleteOptimization',
    async (id: number, thunkAPI) => {
        try {
            // Send a POST request to delete an optimization
            const response = await axiosInstance.post(
                EndPoints.DELETE_OPTIMIZATION + `?id=${id}`,
                null,
                authHeader()
            );
            return await response.data;
        } catch (error) {
            // Return a rejected value if the request fails
            return thunkAPI.rejectWithValue(null);
        }
    }
);

// Async thunk to fetch details of a specific optimization
export const GetOptimizationDetails = createAsyncThunk(
    'GetOptimizationDetails',
    async (id: number, thunkAPI) => {
        try {
            // Send a GET request to fetch optimization details
            const response = await axiosInstance.get(
                EndPoints.GET_OPTIMIZATION_DETAILS + `?id=${id}`,
                authHeader()
            );
            return await response.data;
        } catch (error) {
            // Return a rejected value if the request fails
            return thunkAPI.rejectWithValue(null);
        }
    }
);

// Create a Redux slice for managing optimization-related state
export const optimizationSlice = createSlice({
    name: 'optimization', // Name of the slice
    initialState: {
        // Define initial state properties
        canBeOptimized: false,
        optimizationColumnsLoading: true,
        optimizationDataInputColumns: [] as any[],
        optimizationDataOutputColumns: [] as string[],
        optimizationInProgress: false,
        optimizationResult: null as any,
        optimizationResultDialogOpen: false,
        optimizationTrainingDatasetId: null,
        optimizationIsShared: false,
        optimizationIsDisabled: false,
        optimizationModels: [],
        deleteOptimizationDialog: { isOpen: false, body: null },
        deletingOptimization: false,
        refreshOptimizationsTable: 0,
        optimizationDetailsDialog: { isOpen: false, body: null },
        optimizationDetailsLoading: true,
        optimizationDetails: null,
        failedOptimizationDialog: { isOpen: false, body: null },
    },
    reducers: {
        // Clear all optimization-related data
        clearOptimizationData: (state) => {
            state.canBeOptimized = false;
            state.optimizationDataInputColumns = [];
            state.optimizationDataOutputColumns = [];
            state.optimizationResult = null;
            state.optimizationResultDialogOpen = false;
            state.optimizationTrainingDatasetId = null;
            state.optimizationIsShared = false;
            state.optimizationIsDisabled = false;
            state.optimizationModels = [];
        },
        // Clear all input column values for optimization
        handleClearAllOptimizationInputColumns: (state) => {
            state.optimizationDataInputColumns = state.optimizationDataInputColumns.map((el) => ({
                ...el,
                value: null,
                varying: false
            }));
        },
        // Toggle the visibility of the single optimization result dialog
        handleToggleSingleOptimizationDialog: (state, action) => {
            state.optimizationResultDialogOpen = action.payload;
        },
        // Clear results of a single optimization
        handleClearSingleOptimizationResults: (state) => { 
            state.optimizationResult = null;
        },
        // Change the "varying" state of a column
        handleChangeColumnVarying: (state, action) => {
            state.optimizationDataInputColumns = state.optimizationDataInputColumns.map((el) => {
                if (el.name === action.payload) {
                    el.varying = !el.varying;
                    el.value = null;
                }
                return el;
            });
        },
        // Save an input value for a specific column
        saveOptimizationValue: (state, action) => {
            const { name, value } = action.payload;
            state.optimizationDataInputColumns = state.optimizationDataInputColumns.map((el) =>
                el.name === name ? { ...el, value } : el
            );
        },
        // Toggle the visibility of the delete optimization dialog
        handleToggleDeleteOptimizationDialog: (state, action) => {
            state.deleteOptimizationDialog = action.payload;
        },
        // Toggle the visibility of the optimization details dialog
        handleToggleOptimizationDetailsDialog: (state, action) => {
            state.optimizationDetailsDialog = action.payload;
        },
        // Refresh Optimizations table
        handleRefreshOptimizationsTable: (state) => {
            state.refreshOptimizationsTable++;
        },
        // Toggles the failed optimization modal visibility
        handleToggleFailedOptimizationModal: (state, action) => {
            state.failedOptimizationDialog = action.payload;
        },
    },
    extraReducers: (builder) => {
        // Add cases for async thunk actions to manage state transitions
        builder.addCase(GetOptimizationColumns.pending, (state) => { 
            state.optimizationColumnsLoading = true;
        });
        builder.addCase(GetOptimizationColumns.fulfilled, (state, action) => { 
            state.optimizationColumnsLoading = false;
            if (action.payload?.success) {
                const result = action.payload.result;
                if (result) {
                    state.canBeOptimized = result.canBeOptimized;
                    state.optimizationDataInputColumns = result.inputColumnsDetails.map((el: any) => ({
                        ...el,
                        value: null,
                        varying: false
                    }));
                    state.optimizationDataOutputColumns = result.request?.outputColumns;
                    state.optimizationTrainingDatasetId = result.request?.mlTrainingDatasetId;
                    state.optimizationIsShared = result.isShared;
                    state.optimizationIsDisabled = result.isDisabled;
                }
            }
        });
        builder.addCase(GetOptimizationColumns.rejected, (state) => { 
            state.optimizationColumnsLoading = false;
        });
        
        builder.addCase(OptimizeSingle.pending, (state) => { 
            state.optimizationInProgress = true;
        });
        builder.addCase(OptimizeSingle.fulfilled, (state, action) => { 
            state.optimizationInProgress = false;
            const result = action.payload;
            if (result?.success) {
                state.refreshOptimizationsTable++;
            }
        });
        builder.addCase(OptimizeSingle.rejected, (state) => { 
            state.optimizationInProgress = false;
        });
        
        builder.addCase(GetOptimizationModels.fulfilled, (state, action) => { 
            state.optimizationModels = action.payload || [];
        });
        
        builder.addCase(DeleteOptimization.pending, (state) => { 
            state.deletingOptimization = true;
        });
        builder.addCase(DeleteOptimization.fulfilled, (state) => { 
            state.deletingOptimization = false;
            state.deleteOptimizationDialog = { isOpen: false, body: null };
            state.refreshOptimizationsTable++;
        });
        builder.addCase(DeleteOptimization.rejected, (state) => { 
            state.deletingOptimization = false;
        });

        builder.addCase(GetOptimizationDetails.pending, (state) => { 
            state.optimizationDetailsLoading = true;
        });
        builder.addCase(GetOptimizationDetails.fulfilled, (state, action) => { 
            state.optimizationDetailsLoading = false;
            if (action.payload?.success) {
                state.optimizationDetails = action.payload.result;
            }
        });
        builder.addCase(GetOptimizationDetails.rejected, (state) => { 
            state.optimizationDetailsLoading = false;
            state.optimizationDetails = null;
        });
    },
});

// Export actions for dispatching
export const { 
    clearOptimizationData,
    handleClearAllOptimizationInputColumns,
    handleToggleSingleOptimizationDialog,
    handleClearSingleOptimizationResults,
    handleChangeColumnVarying,
    saveOptimizationValue,
    handleToggleDeleteOptimizationDialog,
    handleToggleOptimizationDetailsDialog,
    handleRefreshOptimizationsTable,
    handleToggleFailedOptimizationModal
} = optimizationSlice.actions;

// Selector to access optimization slice state
export const optimizationSelector = (state: RootState) => state.optimization;